import {
  ApartmentOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  DollarOutlined,
  DownOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PictureOutlined,
  ProjectOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Dropdown,
  Layout,
  Menu,
  Typography,
} from '@wanjianshi/admin-ui';
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/auth/store';
import { useAdminThemeMode } from '@/app/theme-mode';
import { AdminThemeSwitch } from '@/components/AdminThemeSwitch';
import { WanjianshiLogo } from '@/components/WanjianshiLogo';
import { AdminCachedOutlet } from '@/features/navigation/AdminCachedOutlet';
import { AdminVisitedTabs } from '@/features/navigation/AdminVisitedTabs';
import { useAdminVisitedTabs } from '@/features/navigation/useAdminVisitedTabs';
import { adminRoutes } from '@/router/routes';
import { formatAdminRoleNames } from '@/utils/adminRole';

const { Header, Sider, Content } = Layout;

function usableAdminAvatarUrl(value?: string | null): string | undefined {
  const url = String(value || '').trim();
  if (!url) return undefined;
  if (/^https?:\/\/[^/]*googleusercontent\.com\//i.test(url)) return undefined;
  return url;
}

function menuIcon(name: string, path?: string): ReactNode {
  const value = `${name} ${path ?? ''}`;
  if (value.includes('工作台') || path === '/') return <DashboardOutlined />;
  if (value.includes('用户') || value.includes('权限') || value.includes('角色')) return <UserOutlined />;
  if (value.includes('部门') || value.includes('岗位')) return <ApartmentOutlined />;
  if (value.includes('会员') || value.includes('团队')) return <TeamOutlined />;
  if (value.includes('项目') || value.includes('画布') || value.includes('生成')) return <ProjectOutlined />;
  if (value.includes('AI') || value.includes('线路') || value.includes('资源') || value.includes('模型')) return <RobotOutlined />;
  if (value.includes('Banner') || value.includes('模板') || value.includes('运营')) return <PictureOutlined />;
  if (value.includes('兑换码') || value.includes('创作者')) return <SafetyCertificateOutlined />;
  if (value.includes('财务') || value.includes('充值') || value.includes('报表') || value.includes('计费')) return <DollarOutlined />;
  if (value.includes('日志')) return <FileTextOutlined />;
  if (value.includes('运维') || value.includes('系统')) return <SettingOutlined />;
  return <AppstoreOutlined />;
}

export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [openMenuKeys, setOpenMenuKeys] = useState<string[]>([]);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const principal = useAdminAuth(state => state.principal);
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const logout = useAdminAuth(state => state.logout);
  const forbiddenMessage = useAdminAuth(state => state.forbiddenMessage);
  const clearForbidden = useAdminAuth(state => state.clearForbidden);
  const { mode: themeMode } = useAdminThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLElement | null>(null);
  const visitedTabs = useAdminVisitedTabs();
  const cachedPageKeys = useMemo(
    () => visitedTabs.tabs.map(tab => tab.id),
    [visitedTabs.tabs],
  );
  const avatarUrl = useMemo(
    () => usableAdminAvatarUrl(principal?.user.avatar),
    [principal?.user.avatar],
  );
  const displayRoles = useMemo(
    () => formatAdminRoleNames(principal?.role_names?.length ? principal.role_names : principal?.roles),
    [principal?.role_names, principal?.roles],
  );

  useEffect(() => setAvatarLoadFailed(false), [avatarUrl]);

  const currentRoute = useMemo(
    () => adminRoutes.find(route => matchPath({ path: route.path, end: true }, location.pathname)),
    [location.pathname],
  );

  const menuItems = useMemo(() => {
    const routeByPath = new Map(
      adminRoutes
        .filter(route => !route.hidden)
        .map(route => [route.path, route] as const),
    );
    const build = (
      nodes: NonNullable<typeof principal>['menus'],
      depth = 0,
    ): NonNullable<MenuProps['items']> => nodes.flatMap(node => {
      const children = build(node.children ?? [], depth + 1);
      const route = node.path ? routeByPath.get(node.path) : undefined;
      const isAdminRootContainer = depth === 0
        && !node.path
        && ['万剪师管理后台', '管理后台'].includes(node.name.trim());
      if (isAdminRootContainer) return build(node.children ?? [], depth);
      if (route && hasPermission(route.permission)) {
        return [{
          key: route.path,
          label: node.name || route.label,
          icon: depth === 0 ? menuIcon(node.name || route.label, route.path) : undefined,
        }];
      }
      if (children.length) {
        return [{
          key: `menu:${node.id}`,
          label: node.name,
          icon: depth === 0 ? menuIcon(node.name) : undefined,
          children,
        }];
      }
      return [];
    });
    return build(principal?.menus ?? []);
  }, [hasPermission, principal?.authz_version]);

  const activeGroupKey = useMemo(() => {
    const group = principal?.menus.find(node => node.name === currentRoute?.group);
    return group ? `menu:${group.id}` : null;
  }, [currentRoute?.group, principal?.authz_version]);

  useEffect(() => {
    if (activeGroupKey) setOpenMenuKeys([activeGroupKey]);
  }, [activeGroupKey]);

  const userMenu: MenuProps = {
    items: [
      {
        key: 'roles',
        type: 'group',
        label: displayRoles.join('、') || '后台管理员',
      },
      { type: 'divider' },
      { key: 'profile', icon: <SettingOutlined />, label: '个人中心' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出后台' },
    ],
    onClick: ({ key }) => {
      if (key === 'profile') navigate('/profile');
      if (key === 'logout') void logout();
    },
  };

  return (
    <Layout className="admin-shell">
      <Sider
        width={248}
        collapsedWidth={76}
        collapsed={collapsed}
        theme={themeMode}
        trigger={null}
        className="admin-sider"
      >
        <div className={`admin-brand${collapsed ? ' admin-brand--collapsed' : ''}`}>
          <WanjianshiLogo compact className="admin-brand__logo" />
          {!collapsed ? (
            <span className="admin-brand__name">
              <strong>万剪师</strong>
              <small>管理端</small>
            </span>
          ) : null}
        </div>
        {!collapsed ? <div className="admin-navigation-label">菜单</div> : null}
        <div className="admin-menu-scroll">
          <Menu
            theme={themeMode}
            mode="inline"
            items={menuItems}
            selectedKeys={[location.pathname]}
            openKeys={openMenuKeys}
            inlineIndent={18}
            onOpenChange={keys => {
              const latest = keys.find(key => !openMenuKeys.includes(String(key)));
              setOpenMenuKeys(latest ? [String(latest)] : []);
            }}
            onClick={({ key }) => navigate(key)}
          />
        </div>
        <div className={`admin-sider__account${collapsed ? ' admin-sider__account--collapsed' : ''}`}>
          <Dropdown menu={userMenu} placement="topLeft" trigger={['click']}>
            <button
              type="button"
              className={`admin-user-menu admin-user-menu--sider${collapsed ? ' admin-user-menu--collapsed' : ''}`}
              aria-label="管理员账户"
            >
              <Avatar
                size={40}
                src={avatarLoadFailed ? undefined : avatarUrl}
                icon={<UserOutlined />}
                onError={() => {
                  setAvatarLoadFailed(true);
                  return false;
                }}
              >
                {principal?.user.nickname?.slice(0, 1)}
              </Avatar>
              {!collapsed ? (
                <>
                  <span className="admin-identity">
                    <Typography.Text>{principal?.user.nickname || principal?.user.username}</Typography.Text>
                    <Typography.Text type="secondary">{displayRoles[0] || '后台管理员'}</Typography.Text>
                  </span>
                  <DownOutlined className="admin-user-menu__arrow" />
                </>
              ) : null}
            </button>
          </Dropdown>
        </div>
      </Sider>
      <Layout className="admin-main-layout">
        <Header className="admin-header">
          <div className="admin-header__left">
            <Button
              type="text"
              className="admin-collapse-button"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              aria-label={collapsed ? '展开菜单' : '收起菜单'}
              onClick={() => setCollapsed(value => !value)}
            />
            <AdminVisitedTabs controller={visitedTabs} />
          </div>
          <div className="admin-header__actions">
            <AdminThemeSwitch />
          </div>
        </Header>
        <Content ref={contentRef} className="admin-content">
          {forbiddenMessage ? (
            <Alert
              type="warning"
              showIcon
              closable
              message="当前后台角色无权执行该操作"
              description={forbiddenMessage}
              onClose={clearForbidden}
              className="admin-permission-alert"
            />
          ) : null}
          <AdminCachedOutlet
            activeKey={location.pathname}
            cacheKeys={cachedPageKeys}
            scrollContainerRef={contentRef}
          />
        </Content>
      </Layout>
    </Layout>
  );
}
